/**
 * templateMigration — align existing items with their category's dynamic
 * field template. CDI's category-driven schema (`item_data` JSON on each
 * item) evolves over time: fields are added, renamed via `LEGACY_KEY_MAP`,
 * or removed. Older items may therefore hold:
 *   • Missing keys (a template field with no value in `item_data`)
 *   • Legacy keys (an old field name that has since been renamed)
 *   • Orphan keys (a value stored under a key no longer in the template)
 *
 * This helper computes a patch per item, PATCHes it via the existing
 * `PATCH /api/items/{id}` route (no new backend routes, no schema
 * changes), and marks the item id in `localStorage.cdi:migrated_item_ids`
 * so the UI can show a `_migrated = true` flag on the client side.
 *
 * System fields (`id`, `collection_id`, `category_id`, `item_name`,
 * `image`, `images`, `fx_rate_purchase`, `fx_rate_sale`, timestamps)
 * live at the top level of the item, not inside `item_data`, so they
 * are never touched.
 */
import { apiClient } from "@/lib/api";
import { getSampleCollectionId } from "@/lib/sampleImport";

export const MIGRATED_IDS_KEY = "cdi:migrated_item_ids";
export const MIGRATION_SESSION_KEY = "cdi:migration_checked_session";

/**
 * Legacy → canonical key mapping applied INSIDE `item_data` before we
 * compare against the template. Extend this list whenever a template
 * field is renamed so historic values transparently follow the rename
 * instead of being dropped as orphans.
 */
export const LEGACY_KEY_MAP = {
  year: "vintage",
  winery: "producer",
  producer_name: "producer",
  bottle_size: "bottle_size_ml",
  region_name: "region",
  country_name: "country",
};

/**
 * Type-driven default for a missing template field. Numeric fields hold
 * `null` (so aggregation code sees "no value" rather than `0`); strings
 * hold `""`; booleans hold `false`. Multi-select stores `[]`.
 */
function defaultForType(fieldType) {
  switch ((fieldType || "text").toLowerCase()) {
    case "number":
    case "currency":
      return null;
    case "checkbox":
      return false;
    case "multi_select":
      return [];
    case "date":
    case "text":
    case "textarea":
    case "dropdown":
    case "image":
    default:
      return "";
  }
}

/**
 * Build a lookup of category id → ordered field-name list from the
 * response of `GET /api/categories/{id}/fields`. Consumers should
 * memoise this — it's a pure transform.
 */
export function indexFieldsByCategory(categoriesWithFields) {
  const idx = {};
  for (const c of categoriesWithFields || []) {
    idx[c.id] = Array.isArray(c.fields) ? c.fields : [];
  }
  return idx;
}

/**
 * Compute the migration patch for a single item. Returns `null` when
 * the item is already aligned (so callers can skip a redundant PATCH).
 *
 * Rules (per spec):
 *   • For every template field: copy value from item_data (via legacy
 *     alias if needed), else fall back to `defaultForType(type)`.
 *   • Drop any key in item_data that is not in the template (after
 *     legacy alias resolution).
 *   • Never touch top-level system fields.
 */
export function computeItemPatch(item, fields) {
  if (!item || !Array.isArray(fields)) return null;
  const src = { ...(item.item_data || {}) };

  // Fold legacy aliases into their canonical keys before compare. If BOTH
  // the legacy and canonical key are present, canonical wins so users
  // don't lose the newer value.
  for (const [legacyKey, canonicalKey] of Object.entries(LEGACY_KEY_MAP)) {
    if (legacyKey in src && !(canonicalKey in src)) {
      src[canonicalKey] = src[legacyKey];
    }
    delete src[legacyKey];
  }

  const nextData = {};
  const templateNames = new Set();
  for (const f of fields) {
    const name = f.field_name;
    if (!name) continue;
    templateNames.add(name);
    nextData[name] = name in src ? src[name] : defaultForType(f.field_type);
  }

  // Detect orphan keys — any key in the incoming item_data that no
  // longer belongs to the template. If none exist AND every template
  // key matches the current value verbatim, no PATCH is required.
  const orphanKeys = Object.keys(src).filter((k) => !templateNames.has(k));
  const hasDiff = orphanKeys.length > 0 || fields.some((f) => {
    const cur = item.item_data && f.field_name in item.item_data
      ? item.item_data[f.field_name]
      : undefined;
    return cur === undefined; // missing = needs migration
  });

  return hasDiff ? { id: item.id, item_data: nextData, orphanKeys } : null;
}

/**
 * Compute the full migration plan across the app.
 * @returns {{ needs: Array, total: number, byCategory: Object }}
 */
export function computeMigrationPlan(items, fieldsByCategoryId) {
  const needs = [];
  const byCategory = {};
  // Skip items belonging to the on-demand Sample cellar — that data is
  // ephemeral demo state, not a real user cellar, and the manifest
  // already ships in canonical shape.
  const sampleId = getSampleCollectionId();
  for (const it of items || []) {
    if (sampleId && it.collection_id === sampleId) continue;
    const catId = it.category_id;
    if (!catId) continue; // Uncategorised items have no template to align to.
    const fields = fieldsByCategoryId[catId];
    if (!fields || !fields.length) continue;
    const patch = computeItemPatch(it, fields);
    if (patch) {
      needs.push({ item: it, patch });
      byCategory[catId] = (byCategory[catId] || 0) + 1;
    }
  }
  return { needs, total: needs.length, byCategory };
}

/**
 * Fetch items + category-with-fields in parallel and compute the plan.
 * Callers pass in an existing apiClient to avoid duplicate axios
 * instances.
 */
export async function loadMigrationPlan() {
  const [itemsRes, catsRes] = await Promise.all([
    apiClient.get("/items"),
    apiClient.get("/categories"),
  ]);
  const items = Array.isArray(itemsRes.data) ? itemsRes.data : [];
  const cats = Array.isArray(catsRes.data) ? catsRes.data : [];
  // Fetch fields per category in parallel — /categories/aggregate doesn't
  // return the full field list, so we hit /categories/{id}/fields.
  const fieldsArr = await Promise.all(
    cats.map((c) =>
      apiClient
        .get(`/categories/${c.id}/fields`)
        .then((r) => ({ id: c.id, fields: r.data || [] }))
        .catch(() => ({ id: c.id, fields: [] })),
    ),
  );
  const fieldsByCategoryId = indexFieldsByCategory(fieldsArr);
  const plan = computeMigrationPlan(items, fieldsByCategoryId);
  return { ...plan, items, fieldsByCategoryId };
}

/**
 * Execute the plan. PATCHes each item sequentially so we don't overload
 * the backend or trigger optimistic-lock issues. Marks each successful
 * id in localStorage so the UI can render a `_migrated` chip.
 */
export async function runMigration(plan, { onProgress } = {}) {
  const migrated = [];
  const failed = [];
  const ids = readMigratedIds();
  for (let i = 0; i < plan.needs.length; i++) {
    const { item, patch } = plan.needs[i];
    try {
      await apiClient.patch(`/items/${item.id}`, { item_data: patch.item_data });
      ids.add(item.id);
      migrated.push(item.id);
    } catch (e) {
      failed.push({ id: item.id, error: e?.message || String(e) });
    }
    if (typeof onProgress === "function") onProgress(i + 1, plan.needs.length);
  }
  writeMigratedIds(ids);
  return { migrated, failed };
}

/** Read the client-side "which items have been migrated" set. */
export function readMigratedIds() {
  if (typeof window === "undefined") return new Set();
  try {
    const raw = window.localStorage.getItem(MIGRATED_IDS_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(arr) ? arr.filter((x) => typeof x === "string") : []);
  } catch { return new Set(); }
}

function writeMigratedIds(set) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(MIGRATED_IDS_KEY, JSON.stringify([...set]));
  } catch { /* quota — best effort */ }
}

/** Decorate an item list with the client-side `_migrated` flag. */
export function decorateWithMigrationFlag(items) {
  const ids = readMigratedIds();
  return (items || []).map((it) => (ids.has(it.id) ? { ...it, _migrated: true } : it));
}
